/**
 * @voltxampere/ui — UndoStack (Sprint 13)
 *
 * Per ADR-0017. Bounded command history (default 50). Each
 * `execute` runs the command's `do`, pushes it onto the undo
 * buffer, and clears redo (the standard Photoshop / VS Code
 * pattern — a fresh edit invalidates the redo branch).
 *
 * Hitting `maxDepth` drops the oldest command from the bottom
 * (FIFO eviction); the dropped command is not destructable —
 * the user just loses the ability to undo *past* the limit.
 *
 * Sprint 19 persistence will add a `serialize() / hydrate()`
 * pair so the stack survives reload; Sprint 13 keeps it
 * memory-only.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const DEFAULT_MAX_DEPTH = 50;
export class UndoStack {
    maxDepth;
    undoBuffer = [];
    redoBuffer = [];
    constructor(maxDepth = DEFAULT_MAX_DEPTH) {
        this.maxDepth = maxDepth;
    }
    /**
     * Run `cmd.do(circuit)`, push the command, return the new
     * circuit. Clears redo — a fresh edit invalidates the
     * forward history.
     */
    execute(cmd, circuit) {
        const next = cmd.do(circuit);
        this.undoBuffer.push(cmd);
        if (this.undoBuffer.length > this.maxDepth) {
            this.undoBuffer.shift();
        }
        this.redoBuffer = [];
        return next;
    }
    /**
     * Pop the most recent command, run its `undo`, push to redo
     * buffer. Returns `null` when the undo buffer is empty.
     */
    undo(circuit) {
        const cmd = this.undoBuffer.pop();
        if (!cmd)
            return null;
        const prev = cmd.undo(circuit);
        this.redoBuffer.push(cmd);
        return prev;
    }
    /**
     * Pop the most recent redo entry, run its `do` again, push
     * back to undo buffer. Returns `null` when redo is empty.
     */
    redo(circuit) {
        const cmd = this.redoBuffer.pop();
        if (!cmd)
            return null;
        const next = cmd.do(circuit);
        this.undoBuffer.push(cmd);
        return next;
    }
    get canUndo() {
        return this.undoBuffer.length > 0;
    }
    get canRedo() {
        return this.redoBuffer.length > 0;
    }
    /** Wipe both buffers. Used by the host when a circuit reloads. */
    clear() {
        this.undoBuffer = [];
        this.redoBuffer = [];
    }
    get size() {
        return this.undoBuffer.length;
    }
    /** Test surface — the redo buffer length. */
    get redoSize() {
        return this.redoBuffer.length;
    }
}
//# sourceMappingURL=undo-stack.js.map